import { format, parseISO, isValid, isToday } from "date-fns";
import {
    ApiBounds,
    ApiConversation,
    ApiMessage,
    ApiFragmentNode,
    ApiSectionGroup,
} from "../shared/interfaces";

/**
 * A server timestamp with its zone stated. The API writes UTC, but not always with a
 * zone, and `new Date("2026-01-24T20:47:14")` reads a zoneless date-time as local time.
 * A date-time without a zone gets "Z"; an offset (with or without a colon) or "Z" stays,
 * and a date-only string, which already parses as UTC, stays as it is.
 */
export function explicitUtc(timestamp: string): string {
    const hasTimezone = /([+-]\d{2}:?\d{2}|Z)$/.test(timestamp);
    return !hasTimezone && timestamp.includes("T") ? `${timestamp}Z` : timestamp;
}

/**
 * Normalize a date string or Date object to ensure consistent timezone handling:
 * a string is read through `explicitUtc`, so a zoneless one is UTC, not local time.
 */
export function normalizeDate(date: Date | string): Date {
    if (typeof date === "string") return new Date(explicitUtc(date));
    return date instanceof Date ? date : new Date(date);
}

function safeDateCompareAsc(a: Date | string, b: Date | string): number {
    return normalizeDate(a).getTime() - normalizeDate(b).getTime();
}

export function comparatorMessageOldToNew(a: ApiMessage, b: ApiMessage): number {
    return safeDateCompareAsc(a.createdAt, b.createdAt);
}

export function comparatorMessageNewToOld(a: ApiMessage, b: ApiMessage): number {
    return safeDateCompareAsc(b.createdAt, a.createdAt);
}

export function conversationLastUpdate(c: ApiConversation) {
    if (!c.messages || c.messages.length === 0) {
        return c.updatedAt;
    }

    return c.messages.sort(comparatorMessageOldToNew)[c.messages.length - 1].createdAt;
}

export function comparatorConversationOldestToNewestUpdate(a: ApiConversation, b: ApiConversation) {
    return safeDateCompareAsc(conversationLastUpdate(a), conversationLastUpdate(b));
}

export function comparatorConversationNewestToOldestUpdate(a: ApiConversation, b: ApiConversation) {
    return safeDateCompareAsc(conversationLastUpdate(b), conversationLastUpdate(a));
}

// Numeric-aware collator for natural sorting of hierarchy IDs
const hierarchyCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export function comparatorSectionByHierarchyId(
    a: { h_id?: string; uuid: string },
    b: { h_id?: string; uuid: string },
): number {
    // Compare hierarchy IDs with numeric awareness (e.g., "1.9" before "1.10")
    const byHid = hierarchyCollator.compare(a.h_id ?? "", b.h_id ?? "");
    // Use UUID as tiebreaker for stable sorting
    return byHid !== 0 ? byHid : a.uuid.localeCompare(b.uuid);
}

export function copyToClipboardOrShowAlert(content: string) {
    navigator.clipboard.writeText(content).catch((_) => alert("Not able to copy."));
}

export function bytesToMegabytes(bytes: number): string {
    const mb = bytes / (1024 * 1024);
    return mb.toFixed(1);
}

/**
 * Format file size from bytes to human-readable format
 */
export function formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

export function safeParseDate(dateStr: string | null): Date | null {
    if (!dateStr || dateStr === "TBD") return null;
    try {
        const date = parseISO(dateStr);
        return isValid(date) ? date : null;
    } catch {
        return null;
    }
}

export function safeParseDateWithFallback(dateStr: string | null, fallback: Date): Date {
    return safeParseDate(dateStr) ?? fallback;
}

function formatDateWithPattern(dateInput: string | Date | null, pattern: string): string {
    if (!dateInput || dateInput === "TBD") return "—";
    try {
        const date = dateInput instanceof Date ? dateInput : parseISO(dateInput);
        return isValid(date) ? format(date, pattern) : String(dateInput);
    } catch {
        return String(dateInput);
    }
}

export function formatDate(dateInput: string | Date | null, includeYear = true): string {
    return formatDateWithPattern(dateInput, includeYear ? "MMM d, yyyy" : "MMM d");
}

export function formatShortDate(dateInput: string | Date | null): string {
    return formatDateWithPattern(dateInput, "M/d/yy");
}

// ACC/Procore-linked revisions carry a calendar date (UTC midnight) — slice
// off the date part so western timezones don't show the prior day. Revisions
// without an external link fall back to created_at, a real instant, and must
// format through the full parseISO result instead.
export function formatRevisionDate(
    revisionDate: string | null | undefined,
    isCalendarDate: boolean,
): string {
    if (!revisionDate) return formatShortDate(null);
    return formatShortDate(isCalendarDate ? revisionDate.slice(0, 10) : revisionDate);
}

/** "9/4" — a revision's month and day, same calendar-vs-instant slicing rule as
 *  `formatRevisionDate`, for a sentence that already names the set. */
export function formatRevisionMonthDay(
    revisionDate: string | null | undefined,
    isCalendarDate: boolean,
): string {
    if (!revisionDate) return "—";
    return formatDateWithPattern(isCalendarDate ? revisionDate.slice(0, 10) : revisionDate, "M/d");
}

/** "Sep 22, 2026" — a revision's full date for a details tooltip, same slicing rule. */
export function formatRevisionLongDate(
    revisionDate: string | null | undefined,
    isCalendarDate: boolean,
): string {
    if (!revisionDate) return "—";
    return formatDate(isCalendarDate ? revisionDate.slice(0, 10) : revisionDate);
}

/** "Aug 12" / "Sep 1" — the Ledger revision cell's no-year date, same
 *  calendar-vs-instant slicing rule as `formatRevisionDate`. */
export function formatRevisionShortDate(
    revisionDate: string | null | undefined,
    isCalendarDate: boolean,
): string {
    if (!revisionDate) return "—";
    return formatDate(isCalendarDate ? revisionDate.slice(0, 10) : revisionDate, false);
}

/** "2:53 PM" for today, "Jul 5" otherwise — for list rows (e.g. the Questions
 * rail) where a bare date reads ambiguously for anything from today. */
export function formatShortTimestamp(dateInput: string | Date | null): string {
    if (!dateInput || dateInput === "TBD") return "—";
    try {
        const date = dateInput instanceof Date ? dateInput : parseISO(dateInput);
        if (!isValid(date)) return String(dateInput);
        return isToday(date) ? format(date, "h:mm a") : format(date, "MMM d");
    } catch {
        return String(dateInput);
    }
}

export function formatDateRange(start: Date | null, end: Date | null): string {
    if (!start || !end) return "—";
    return `${format(start, "M/d/yy")} - ${format(end, "M/d/yy")}`;
}

export const BOUNDS_ID_PRECISION = 4;

export const getIdForBounds = (bounds: ApiBounds): string => {
    const p = BOUNDS_ID_PRECISION;
    return (
        `annot-${bounds.page}-${bounds.xMin.toFixed(p)}-` +
        `${bounds.yMin.toFixed(p)}-${bounds.xMax.toFixed(p)}-${bounds.yMax.toFixed(p)}`
    );
};

/**
 * Serialize a fragment node for deep comparison
 * Used in component mode to detect changes and trigger re-renders
 */
export function serializeFragmentNode(node: ApiFragmentNode): string {
    return JSON.stringify({
        id: node.id,
        parentUuid: node.parentUuid ?? null,
        hierarchyId: node.hierarchyId,
        title: node.title,
        rawTitle: node.rawTitle ?? null,
        titleTextbox: node.titleTextbox ?? null,
        idTextbox: node.idTextbox ?? null,
        destinationName: node.destinationName ?? null,
        location: node.location ?? null,
        drawingType: node.drawingType ?? null,
        // Include componentType + tableConfig so saves that only change these
        // fields don't get skipped by the component-mode store-update bail-out
        // (nodesShallowEqual → useComponentMode.useEffect skips setComponents-
        // ForPage when serialized strings match → stale UI until refresh).
        componentType: node.componentType ?? null,
        tableConfig: node.tableConfig ?? null,
        bounds: node.bounds,
        isGroup: node.isGroup ?? false,
    });
}

/**
 * Deep equality check for fragment node arrays
 */
export function areFragmentNodeArraysEqual(a: ApiFragmentNode[], b: ApiFragmentNode[]): boolean {
    if (a.length !== b.length) {
        return false;
    }
    return a.every(
        (node, index) => serializeFragmentNode(node) === serializeFragmentNode(b[index]),
    );
}

/**
 * Serialize a section group for deep comparison
 */
function serializeSectionGroup(group: ApiSectionGroup | null): string {
    if (!group) {
        return "null";
    }
    return JSON.stringify({
        id: group.id,
        start_page_in_file: group.start_page_in_file,
        group_code: group.group_code,
        group_title: group.group_title,
        title_textbox: group.title_textbox ?? null,
        id_textbox: group.id_textbox ?? null,
    });
}

/**
 * Deep equality check for section groups
 */
export function areSectionGroupsEqual(
    a: ApiSectionGroup | null,
    b: ApiSectionGroup | null,
): boolean {
    return serializeSectionGroup(a) === serializeSectionGroup(b);
}
